import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateModule } from '@ngx-translate/core';
import { SHARED_COMPONENTS } from '../../../../../shared/shared-imports';
import { ApiService } from '../../../../../core/services/api.service';
import { ApiEndPoints, ApiRoot } from '../../../../../shared/constants/api-endpoints';
import { SharedService } from '../../../../../shared/services/shared.service';

type InvoiceOcrStatus = 'idle' | 'uploading' | 'uploaded' | 'success' | 'error';

interface InvoiceOcrPayloadFile {
  file: string;
}

interface InvoiceOcrPayloadProperties {
  files: InvoiceOcrPayloadFile[];
  lang: string;
  userId?: string;
}

interface InvoiceOcrFieldValue<T = string | number> {
  value: T;
  accuracyRate: number;
}

interface InvoiceOcrUploadedFileItem {
  id: string;
  name: string;
  sizeLabel: string;
  sourceFile: File;
  base64: string;
  previewUrl: string;
  safePreviewUrl: SafeResourceUrl;
}

interface InvoiceOcrApiProduct {
  ProductCountryOfOrigin?: InvoiceOcrFieldValue<string>;
  ProductQuantity?: InvoiceOcrFieldValue<number>;
  HScode?: InvoiceOcrFieldValue<string>;  
  ProductQuantityUnit?: InvoiceOcrFieldValue<string>;
}

export interface InvoiceOcrExtractedProduct {
  country: string;
  quantity: string;
  hsCode: string;
  unit: string;
}

interface InvoiceOcrApiInvoice {
  InvoiceNumber?: InvoiceOcrFieldValue<string>;
  InvoiceDate?: InvoiceOcrFieldValue<string>;
  Amount?: InvoiceOcrFieldValue<number>;
  Currency?: InvoiceOcrFieldValue<string>;
  ModeOfTransportation?: InvoiceOcrFieldValue<string>;
  ExitPoint?: InvoiceOcrFieldValue<string>;
  ShippingFrom?: InvoiceOcrFieldValue<string>;
  PortofDischarge?: InvoiceOcrFieldValue<string>;
  DestinationCountry?: InvoiceOcrFieldValue<string>;
  DestinationAndPort?: InvoiceOcrFieldValue<string>;
  HSCode?: InvoiceOcrFieldValue<string>;
  Products?: InvoiceOcrApiProduct[];
}

interface InvoiceOcrApiResponse {
  request_id?: string;
  status?: string;
  isGSP?: boolean;
  invoice?: InvoiceOcrApiInvoice;
  gspEligibility?: InvoiceOcrApiGspEligibility;
  avgAccuracyRate?: number;
  code?: number | string;
  message?: string;
  estProcessingTime?: number | string;
}

interface InvoiceOcrApiGspEligibilityField {
  value?: string | number | null;
}

interface InvoiceOcrApiGspEligibilityProduct {
  label?: string | null;
  hsCode?: string | number | null;
}

interface InvoiceOcrApiGspEligibilityCarriedField {
  label?: string | null;
  value?: string | number | null;
}

interface InvoiceOcrApiGspEligibility {
  destination?: InvoiceOcrApiGspEligibilityField;
  origin?: InvoiceOcrApiGspEligibilityField;
  benefit?: InvoiceOcrApiGspEligibilityField;
  reason?: string | null;
  fieldsPrefilled?: {
    readyFields?: number | null;
    totalFields?: number | null;
  };
  product?: InvoiceOcrApiGspEligibilityProduct[];
  carriedOverFields?: InvoiceOcrApiGspEligibilityCarriedField[];
}

interface InvoiceOcrGspCarriedField {
  label: string;
  value: string;
}

interface InvoiceOcrGspModalData {
  destination: string;
  product: string;
  agreement: string;
  benefit: string;
  carriedOverFields: InvoiceOcrGspCarriedField[];
  fieldsPrefilled: string;
}

export interface InvoiceOcrExtractionResult {
  invoiceNo: string;
  invoiceDate: {
    day: string | null;
    month: string | null;
    year: string | null;
  };
  invoiceDateRaw?: string;
  invoiceDateOutOfRange?: boolean;
  invCurrency: string;
  invAmount: string;
  transportMode?: string;
  exitPoint?: string;
  shippingFrom?: string;
  portOfDischarge?: string;
  destinationCountry?: string;
  destinationAndPort?: string;
  hsCode?: string;
  products?: InvoiceOcrExtractedProduct[];
}

interface InvoiceOcrParsedDateResult {
  value: InvoiceOcrExtractionResult['invoiceDate'];
  raw: string;
  isOutOfRange: boolean;
}

@Component({
  selector: 'app-invoice-ocr',
  imports: [TranslateModule, ...SHARED_COMPONENTS],
  templateUrl: './invoice-ocr.component.html'
})
export class InvoiceOcrComponent implements OnDestroy, AfterViewChecked {
  @Input() allowGspSwitch: boolean = false;
  @Output() ocrExtracted = new EventEmitter<InvoiceOcrExtractionResult>();
  @Output() ocrFilesReadyForUpload = new EventEmitter<File[]>();
  @Output() ocrCompletedChange = new EventEmitter<boolean>();
  @Output() manualEntrySelected = new EventEmitter<void>();
  @Output() redoRequested = new EventEmitter<void>();
  @Output() switchToGsp = new EventEmitter<void>();

  showInvoiceOcrModal: boolean = false;
  showInvoiceOcrEstimatedWaitModal: boolean = false;
  showInvoiceOcrScanningModal: boolean = false;
  showInvoiceOcrErrorModal: boolean = false;
  showInvoiceOcrServiceChoiceModal: boolean = false;
  invoiceOcrStatus: InvoiceOcrStatus = 'idle';
  invoiceOcrProgress: number = 0;
  invoiceOcrScanProgress: number = 0;
  invoiceOcrUploadErrorKey: string = '';
  invoiceOcrErrorTitleKey: string = 'COO.OCR.ERRORS.GENERIC.TITLE';
  invoiceOcrErrorMessageKey: string = 'COO.OCR.ERRORS.GENERIC.MESSAGE';
  invoiceOcrUploadedFiles: InvoiceOcrUploadedFileItem[] = [];
  selectedPreviewFileId: string = '';
  invoiceOcrPayload: { properties: InvoiceOcrPayloadProperties } = {
    properties: {
      files: [],
      lang: 'EN'
    }
  };
  invoiceOcrCompleted: boolean = false;
  invoiceOcrAccuracy: number = 92;
  invoiceOcrRequestId: string = '';
  invoiceOcrEstimatedWaitMinutes: number = 3;
  invoiceOcrGspModalData: InvoiceOcrGspModalData = {
    destination: '',
    product: '',
    agreement: '',
    benefit: '',
    carriedOverFields: [],
    fieldsPrefilled: ''
  };

  @ViewChild('invoiceOcrUploadDialog') invoiceOcrUploadDialog?: ElementRef<HTMLDialogElement>;
  @ViewChild('invoiceOcrUploadInput') invoiceOcrUploadInput?: ElementRef<HTMLInputElement>;
  @ViewChild('invoiceOcrEstimatedWaitDialog') invoiceOcrEstimatedWaitDialog?: ElementRef<HTMLDialogElement>;
  @ViewChild('invoiceOcrScanningDialog') invoiceOcrScanningDialog?: ElementRef<HTMLDialogElement>;
  @ViewChild('invoiceOcrErrorDialog') invoiceOcrErrorDialog?: ElementRef<HTMLDialogElement>;
  @ViewChild('invoiceOcrGspDialog') invoiceOcrGspDialog?: ElementRef<HTMLDialogElement>;

  private invoiceOcrUploadTimer?: number;
  private invoiceOcrScanTimer?: number;
  private invoiceOcrScanCompletionTimer?: number;
  private invoiceOcrPollingTimer?: number;

  constructor(
    private api: ApiService,
    private sharedService: SharedService,
    private sanitizer: DomSanitizer
  ) { }

  ngAfterViewChecked(): void {
    this.syncDialogState(this.invoiceOcrUploadDialog, this.showInvoiceOcrModal);
    this.syncDialogState(this.invoiceOcrEstimatedWaitDialog, this.showInvoiceOcrEstimatedWaitModal);
    this.syncDialogState(this.invoiceOcrScanningDialog, this.showInvoiceOcrScanningModal);
    this.syncDialogState(this.invoiceOcrErrorDialog, this.showInvoiceOcrErrorModal);
    this.syncDialogState(this.invoiceOcrGspDialog, this.showInvoiceOcrServiceChoiceModal);
  }

  openInvoiceOcrModal() {
    if (this.invoiceOcrStatus === 'error' && this.invoiceOcrUploadedFiles.length) {
      this.resetInvoiceOcrFilesState();
    }
    this.showInvoiceOcrModal = true;
    this.invoiceOcrUploadErrorKey = '';
    this.resetInvoiceOcrErrorState();
  }

  openInvoiceOcrFilePicker() {
    this.invoiceOcrUploadInput?.nativeElement?.click();
  }

  closeInvoiceOcrModal() {
    if (this.invoiceOcrStatus === 'uploading') {
      return;
    }
    this.cancelInvoiceOcrModal();
  }

  get selectedPreviewFile(): InvoiceOcrUploadedFileItem | null {
    if (!this.invoiceOcrUploadedFiles.length) {
      return null;
    }

    return this.invoiceOcrUploadedFiles.find((file) => file.id === this.selectedPreviewFileId)
      ?? this.invoiceOcrUploadedFiles[0];
  }

  cancelInvoiceOcrModal() {
    if (this.invoiceOcrStatus === 'uploading') {
      return;
    }
    this.resetInvoiceOcrFilesState();
    this.showInvoiceOcrModal = false;
    this.invoiceOcrUploadErrorKey = '';
    this.resetInvoiceOcrErrorState();
  }

  async onInvoiceOcrFileSelected(event: Event) {
    const input = event.target as HTMLInputElement | null;
    const files = input?.files ? Array.from(input.files) : [];

    if (!files.length) {
      return;
    }

    this.invoiceOcrUploadErrorKey = '';
    this.resetInvoiceOcrErrorState();
    this.invoiceOcrCompleted = false;
    this.ocrCompletedChange.emit(false);
    this.showInvoiceOcrErrorModal = false;
    this.invoiceOcrStatus = 'uploading';
    this.invoiceOcrProgress = 0;
    this.clearInvoiceOcrTimers();

    const validFiles = this.getValidInvoiceOcrFiles(files);

    if (!validFiles.length) {
      this.invoiceOcrStatus = 'idle';
      this.invoiceOcrProgress = 0;
      this.invoiceOcrUploadErrorKey = this.getInvoiceOcrSelectionErrorKey(files);
      if (input) {
        input.value = '';
      }
      return;
    }

    if (validFiles.length !== files.length) {
      this.invoiceOcrUploadErrorKey = this.getInvoiceOcrSelectionErrorKey(files);
    }

    try {
      const uploadedFiles = await Promise.all(
        validFiles.map(async (file, index): Promise<InvoiceOcrUploadedFileItem> => {
          const base64 = await this.readFileAsDataUrl(file);
          const previewUrl = URL.createObjectURL(file);
          return {
            id: `${Date.now()}-${index}-${file.name}`,
            name: file.name,
            sizeLabel: this.formatInvoiceOcrFileSize(file.size),
            sourceFile: file,
            base64,
            previewUrl,
            safePreviewUrl: this.sanitizer.bypassSecurityTrustResourceUrl(previewUrl)
          };
        })
      );

      this.invoiceOcrUploadedFiles = [
        ...this.invoiceOcrUploadedFiles,
        ...uploadedFiles
      ];
      this.selectedPreviewFileId = this.selectedPreviewFileId || this.invoiceOcrUploadedFiles[0]?.id || '';
    } catch {
      this.invoiceOcrStatus = this.invoiceOcrUploadedFiles.length ? 'uploaded' : 'idle';
      this.invoiceOcrProgress = this.invoiceOcrUploadedFiles.length ? 100 : 0;
      this.invoiceOcrUploadErrorKey = 'COO.OCR.INVALID_FILE_TYPE';
      if (input) {
        input.value = '';
      }
      return;
    }

    this.invoiceOcrUploadTimer = window.setInterval(() => {
      this.invoiceOcrProgress = Math.min(this.invoiceOcrProgress + 20, 100);
      if (this.invoiceOcrProgress >= 100) {
        if (this.invoiceOcrUploadTimer) {
          window.clearInterval(this.invoiceOcrUploadTimer);
          this.invoiceOcrUploadTimer = undefined;
        }
        this.invoiceOcrStatus = 'uploaded';
      }
    }, 180);

    if (input) {
      input.value = '';
    }
  }

  removeInvoiceOcrFile(fileId: string) {
    this.invoiceOcrUploadedFiles
      .filter((file) => file.id === fileId)
      .forEach((file) => URL.revokeObjectURL(file.previewUrl));

    this.invoiceOcrUploadedFiles = this.invoiceOcrUploadedFiles.filter(
      (file) => file.id !== fileId
    );

    if (this.selectedPreviewFileId === fileId) {
      this.selectedPreviewFileId = this.invoiceOcrUploadedFiles[0]?.id || '';
    }

    if (this.invoiceOcrUploadedFiles.length) {
      this.invoiceOcrStatus = 'uploaded';
      this.invoiceOcrProgress = 100;
      return;
    }

    this.resetInvoiceOcrFilesState();
  }

  selectPreviewFile(fileId: string) {
    this.selectedPreviewFileId = fileId;
  }

  scanInvoiceWithOcr() {
    if (this.invoiceOcrStatus !== 'uploaded' || !this.invoiceOcrUploadedFiles.length) {
      this.invoiceOcrUploadErrorKey = 'COO.OCR.FILE_REQUIRED';
      return;
    }

    this.invoiceOcrUploadErrorKey = '';
    this.resetInvoiceOcrErrorState();
    this.showInvoiceOcrScanningModal = true;
    this.invoiceOcrScanProgress = 12;
    this.clearInvoiceOcrTimers();
    this.startInvoiceOcrScanProgress();
    this.getInvoceReqId();
  }

  retryInvoiceOcr() {
    this.showInvoiceOcrErrorModal = false;
    this.resetInvoiceOcrErrorState();
    if (!this.invoiceOcrUploadedFiles.length) {
      this.openInvoiceOcrModal();
      return;
    }
    this.invoiceOcrStatus = 'uploaded';
    this.scanInvoiceWithOcr();
  }

  dismissInvoiceOcrEstimatedWaitModal() {
    this.showInvoiceOcrEstimatedWaitModal = false;
    this.showInvoiceOcrScanningModal = false;
    this.invoiceOcrScanProgress = 0;
    this.clearInvoiceOcrTimers();
    if (this.invoiceOcrUploadedFiles.length) {
      this.invoiceOcrStatus = 'uploaded';
      this.invoiceOcrProgress = 100;
      this.showInvoiceOcrModal = true;
      return;
    }

    this.invoiceOcrStatus = 'idle';
    this.invoiceOcrProgress = 0;
  }

  continueInvoiceOcrAfterEstimatedWait() {
    this.showInvoiceOcrEstimatedWaitModal = false;
    this.showInvoiceOcrScanningModal = true;
    if (!this.invoiceOcrRequestId) {
      this.showInvoiceOcrErrorByCode(null);
      return;
    }
    this.processInvoiceOcrRequest(this.invoiceOcrRequestId);
  }

  enterInvoiceDetailsManually() {
    this.showInvoiceOcrEstimatedWaitModal = false;
    this.showInvoiceOcrScanningModal = false;
    this.showInvoiceOcrErrorModal = false;
    this.showInvoiceOcrModal = false;
    this.resetInvoiceOcrErrorState();
    this.clearInvoiceOcrTimers();
    this.invoiceOcrStatus = this.invoiceOcrUploadedFiles.length ? 'uploaded' : 'idle';
    this.manualEntrySelected.emit();
  }

  dismissInvoiceOcrErrorModal() {
    this.showInvoiceOcrErrorModal = false;
    if (this.invoiceOcrUploadedFiles.length) {
      this.invoiceOcrStatus = 'uploaded';
      this.invoiceOcrProgress = 100;
    }
    this.resetInvoiceOcrErrorState();
  }

  continueWithCooAfterOcr() {
    this.showInvoiceOcrServiceChoiceModal = false;
  }

  switchToGspAfterOcr() {
    this.showInvoiceOcrServiceChoiceModal = false;
    this.switchToGsp.emit();
  }

  redoInvoiceOcr() {
    this.redoRequested.emit();
    this.invoiceOcrCompleted = false;
    this.ocrCompletedChange.emit(false);
    this.resetInvoiceOcrFilesState();
  }

  ngOnDestroy(): void {
    this.clearInvoiceOcrTimers();
    this.closeDialog(this.invoiceOcrUploadDialog);
    this.closeDialog(this.invoiceOcrEstimatedWaitDialog);
    this.closeDialog(this.invoiceOcrScanningDialog);
    this.closeDialog(this.invoiceOcrErrorDialog);
    this.closeDialog(this.invoiceOcrGspDialog);
  }

  private completeInvoiceOcrScan(response: InvoiceOcrApiResponse | null) {
    this.clearInvoiceOcrTimers();

    if (!response || response.status !== 'success' || !response.invoice) {
      this.invoiceOcrScanProgress = 0;
      this.showInvoiceOcrScanningModal = false;
      this.invoiceOcrStatus = 'error';
      this.invoiceOcrCompleted = false;
      this.ocrCompletedChange.emit(false);
      this.showInvoiceOcrErrorByCode(400);
      return;
    }

    this.invoiceOcrScanProgress = 100;
    this.showInvoiceOcrScanningModal = false;
    const result = this.buildInvoiceOcrResult(response);
    this.ocrExtracted.emit(result);
    this.ocrFilesReadyForUpload.emit(
      this.invoiceOcrUploadedFiles.map((uploadedFile) => uploadedFile.sourceFile)
    );
    this.clearInvoiceOcrSelectedFiles();
    this.invoiceOcrStatus = 'success';
    this.invoiceOcrCompleted = true;
    this.invoiceOcrAccuracy = this.normalizeAccuracyRate(response.avgAccuracyRate);
    this.invoiceOcrGspModalData = this.buildInvoiceOcrGspModalData(response);
    this.ocrCompletedChange.emit(true);
    this.showInvoiceOcrModal = false;
    //this.showInvoiceOcrServiceChoiceModal = this.isInvoiceOcrGspEligible(response.isGSP) && this.allowGspSwitch;
  }

  private buildInvoiceOcrResult(response: InvoiceOcrApiResponse): InvoiceOcrExtractionResult {
    const invoice = response.invoice || {};
    const parsedDate = this.parseInvoiceOcrDate(invoice.InvoiceDate?.value);

    return {
      invoiceNo: this.readInvoiceOcrString(invoice.InvoiceNumber?.value),
      invoiceDate: parsedDate.value,
      invoiceDateRaw: parsedDate.raw,
      invoiceDateOutOfRange: parsedDate.isOutOfRange,
      invCurrency: this.readInvoiceOcrString(invoice.Currency?.value),
      invAmount: this.readInvoiceOcrAmount(invoice.Amount?.value),
      transportMode: this.readInvoiceOcrString(invoice.ModeOfTransportation?.value),
      exitPoint: this.readInvoiceOcrString(invoice.ExitPoint?.value),
      shippingFrom: this.readInvoiceOcrString(invoice.ShippingFrom?.value),
      portOfDischarge: this.readInvoiceOcrString(invoice.PortofDischarge?.value),
      destinationCountry: this.readInvoiceOcrString(invoice.DestinationCountry?.value),
      destinationAndPort: this.readInvoiceOcrString(invoice.DestinationAndPort?.value),
      hsCode: this.readInvoiceOcrString(invoice.HSCode?.value),
      products: (invoice.Products || []).map((product) => ({
        country: this.readInvoiceOcrString(product.ProductCountryOfOrigin?.value),
        quantity: this.readInvoiceOcrAmount(product.ProductQuantity?.value),
        hsCode: this.readInvoiceOcrString(product.HScode?.value),
        unit: this.readInvoiceOcrString(product.ProductQuantityUnit?.value)
      }))
    };
  }

  private formatInvoiceOcrFileSize(fileSize: number): string {
    if (fileSize < 1024 * 1024) {
      return `${Math.max(1, Math.round(fileSize / 1024))} KB`;
    }
    return `${(fileSize / (1024 * 1024)).toFixed(1)} MB`;
  }

  private getValidInvoiceOcrFiles(files: File[]): File[] {
    const allowedMimeTypes = ['application/pdf'];
    const maxSizeInBytes = 20 * 1024 * 1024;

    return files.filter((file) => {
      const isValidMimeType = allowedMimeTypes.includes(file.type);
      const hasSupportedExtension = /\.(pdf)$/i.test(file.name);
      return (isValidMimeType || hasSupportedExtension) && file.size <= maxSizeInBytes;
    });
  }

  private getInvoiceOcrSelectionErrorKey(files: File[]): string {
    const allowedMimeTypes = ['application/pdf'];
    const maxSizeInBytes = 20 * 1024 * 1024;

    if (
      files.some((file) => {
        const isValidMimeType = allowedMimeTypes.includes(file.type);
        const hasSupportedExtension = /\.(pdf)$/i.test(file.name);
        return !isValidMimeType && !hasSupportedExtension;
      })
    ) {
      return 'COO.OCR.INVALID_FILE_TYPE';
    }

    if (files.some((file) => file.size > maxSizeInBytes)) {
      return 'COO.OCR.FILE_TOO_LARGE';
    }

    return 'COO.OCR.FILE_REQUIRED';
  }

  private readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.onload = () => {
        const result = String(fileReader.result || '');
        resolve(result.includes(',') ? result.split(',')[1] : result);
      };
      fileReader.onerror = () => reject(fileReader.error);
      fileReader.readAsDataURL(file);
    });
  }

  private getInvoceReqId() {
    const selectedLang = (localStorage.getItem('selectedLang') || 'en').toLowerCase();
    this.invoiceOcrPayload = {
      properties: {
        files: this.invoiceOcrUploadedFiles.map((uploadedFile) => ({
          file: uploadedFile.base64
        })),
        lang: selectedLang === 'ar' ? 'AR' : 'EN'
        // userId can be added here when required by API contract
      }
    };
    this.api.post(
      ApiRoot.INVOICE_OCR_SERVICE,
      ApiEndPoints.CREATE_REQ,
      this.invoiceOcrPayload
      ).subscribe({
        next: (response: any) => {
          if (response && response['request_id']) {
            this.invoiceOcrRequestId = response['request_id'];
            const estProcessingSeconds = Number(response?.estProcessingTime);
            if (Number.isFinite(estProcessingSeconds) && estProcessingSeconds >= 180) {
              this.invoiceOcrEstimatedWaitMinutes = this.resolveEstimatedWaitMinutes(response?.estProcessingTime);
              this.showInvoiceOcrScanningModal = false;
              this.showInvoiceOcrEstimatedWaitModal = true;
              return;
            }
            this.processInvoiceOcrRequest(this.invoiceOcrRequestId);
            return;
          }
          this.showInvoiceOcrErrorByCode(this.getInvoiceOcrResponseCode(response));
        },
      error: (error: any) => {
        this.showInvoiceOcrErrorByCode(this.getInvoiceOcrResponseCode(error));
      }
    });
  }

  private resolveEstimatedWaitMinutes(estProcessingTime: number | string | undefined): number {
    const seconds = Number(estProcessingTime);
    if (!Number.isFinite(seconds) || seconds <= 0) {
      return 3;
    }

    return Math.max(1, Math.ceil(seconds / 60));
  }

  private processInvoiceOcrRequest(requestId: string) {
    if(!this.sharedService.getUserProfile()['Login Name']){
      return;
    }
    const payload = {
      properties: {
        request_id: requestId,
        userId: this.sharedService.getUserProfile()['Login Name']
      }
    };

    this.api.post(
      ApiRoot.INVOICE_OCR_SERVICE,
      ApiEndPoints.INVOICE_PROCESSING,
      payload
    ).subscribe({
      next: (response: any) => {
        const ocrResponse = response as InvoiceOcrApiResponse;
        const status = (ocrResponse?.status || '').toLowerCase();
        const code = Number(ocrResponse?.code);

        if (status === 'success' && code === 200) {
          this.completeInvoiceOcrScan(ocrResponse);
          return;
        }

        if (status === 'running') {
          this.invoiceOcrPollingTimer = window.setTimeout(() => {
            this.processInvoiceOcrRequest(requestId);
          }, 2000);
          return;
        }

        if (code && code !== 200) {
          this.showInvoiceOcrErrorByCode(code);
          return;
        }

        if (status === 'error') {
          this.showInvoiceOcrErrorByCode(code || 400);
          return;
        }

        this.showInvoiceOcrErrorByCode(code || null);
      },
      error: (error: any) => {
        this.showInvoiceOcrErrorByCode(this.getInvoiceOcrResponseCode(error));
      }
    });
  }

  private startInvoiceOcrScanProgress() {
    this.invoiceOcrScanTimer = window.setInterval(() => {
      if (this.invoiceOcrScanProgress >= 96) {
        this.invoiceOcrScanProgress = 18;
        return;
      }

      const progressStep = this.invoiceOcrScanProgress >= 82 ? 4 : 9;
      this.invoiceOcrScanProgress = Math.min(this.invoiceOcrScanProgress + progressStep, 96);
    }, 450);
  }

  private buildInvoiceOcrGspModalData(response: InvoiceOcrApiResponse): InvoiceOcrGspModalData {
    const gspEligibility = response.gspEligibility;
    const destination = this.readInvoiceOcrString(gspEligibility?.destination?.value);
    const origin = this.readInvoiceOcrString(gspEligibility?.origin?.value);
    const benefit = this.readInvoiceOcrString(gspEligibility?.benefit?.value);
    const product = (gspEligibility?.product || [])
      .map((item) => this.readInvoiceOcrString(item.label))
      .filter(Boolean)
      .join(', ');
    const agreement = [origin, destination].filter(Boolean).join('–');
    const readyFields = Number(gspEligibility?.fieldsPrefilled?.readyFields ?? 0);
    const totalFields = Number(gspEligibility?.fieldsPrefilled?.totalFields ?? 0);
    const fieldsPrefilled = readyFields || totalFields ? `${readyFields} of ${totalFields}` : '';
    const carriedOverFields = (gspEligibility?.carriedOverFields || [])
      .map((field) => ({
        label: this.readInvoiceOcrString(field.label),
        value: this.readInvoiceOcrString(field.value)
      }))
      .filter((field) => field.label || field.value);

    return {
      destination,
      product,
      agreement,
      benefit,
      carriedOverFields,
      fieldsPrefilled
    };
  }

  private parseInvoiceOcrDate(rawDate: string | undefined): InvoiceOcrParsedDateResult {
    const normalized = (rawDate || '').trim();
    const match = normalized.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (match) {
      const day = match[1];
      const month = match[2];
      const year = match[3];
      const parsedDate = new Date(Number(year), Number(month) - 1, Number(day));
      const today = new Date();
      // const oneYearAgo = new Date(today.getFullYear(), today.getMonth() - 12, today.getDate());
      // const displayDate = `${match[3]}/${match[2]}/${match[1]}`;
      const oneYearAgo = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
      const displayDate = `${day}/${month}/${year}`;

      if (parsedDate < oneYearAgo) {
        return {
          value: {
            day: null,
            month: null,
            year: null
          },
          raw: displayDate,
          isOutOfRange: true
        };
      }

      return {
        value: {
          year,
          month,
          day
        },
        raw: displayDate,
        isOutOfRange: false
      };
    }

    return {
      value: {
        day: null,
        month: null,
        year: null
      },
      raw: normalized,
      isOutOfRange: false
    };
  }

  private readInvoiceOcrString(value: string | number | undefined | null): string {
    return (value ?? '').toString().trim();
  }

  private readInvoiceOcrAmount(value: string | number | undefined | null): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    return Number(value).toString();
  }

  private normalizeAccuracyRate(value: number | undefined): number {
    if (value === undefined || value === null || Number.isNaN(value)) {
      return 0;
    }

    const percent = value <= 1
      ? Math.round(value * 100)
      : Math.round(value);

    return Math.max(0, Math.min(100, percent));
  }

  private isInvoiceOcrGspEligible(value: unknown): boolean {
    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'string') {
      return value.trim().toLowerCase() === 'true';
    }

    return false;
  }

  private resetInvoiceOcrFilesState() {
    this.clearInvoiceOcrTimers();
    this.invoiceOcrUploadedFiles.forEach((file) => URL.revokeObjectURL(file.previewUrl));
    this.invoiceOcrStatus = 'idle';
    this.invoiceOcrProgress = 0;
    this.invoiceOcrScanProgress = 0;
    this.invoiceOcrEstimatedWaitMinutes = 3;
    this.invoiceOcrUploadErrorKey = '';
    this.resetInvoiceOcrErrorState();
    this.invoiceOcrUploadedFiles = [];
    this.selectedPreviewFileId = '';
    this.invoiceOcrPayload = {
      properties: {
        files: [],
        lang: 'EN'
      }
    };
    this.invoiceOcrRequestId = '';
    this.invoiceOcrGspModalData = {
      destination: '',
      product: '',
      agreement: '',
      benefit: '',
      carriedOverFields: [],
      fieldsPrefilled: ''
    };
    this.ocrCompletedChange.emit(false);
    this.showInvoiceOcrEstimatedWaitModal = false;
    this.showInvoiceOcrErrorModal = false;
  }

  private clearInvoiceOcrSelectedFiles() {
    this.invoiceOcrUploadedFiles.forEach((file) => URL.revokeObjectURL(file.previewUrl));
    this.invoiceOcrUploadedFiles = [];
    this.selectedPreviewFileId = '';
    this.invoiceOcrPayload = {
      properties: {
        files: [],
        lang: 'EN'
      }
    };
    this.invoiceOcrRequestId = '';
    this.invoiceOcrProgress = 0;
  }

  private resetInvoiceOcrErrorState() {
    this.invoiceOcrErrorTitleKey = 'COO.OCR.ERRORS.GENERIC.TITLE';
    this.invoiceOcrErrorMessageKey = 'COO.OCR.ERRORS.GENERIC.MESSAGE';
  }

  private showInvoiceOcrErrorByCode(code: number | null) {
    this.clearInvoiceOcrTimers();
    this.invoiceOcrScanProgress = 0;
    this.showInvoiceOcrEstimatedWaitModal = false;
    this.showInvoiceOcrScanningModal = false;
    this.invoiceOcrStatus = 'error';
    this.invoiceOcrCompleted = false;
    this.ocrCompletedChange.emit(false);

    const errorCode = code ?? 0;
    let errorType = 'GENERIC';

    if ([400, 401, 403, 404, 429].includes(errorCode)) {
      errorType = 'SCAN_FAILED';
    } else if (errorCode === 422) {
      errorType = 'MULTIPLE_INVOICES';
    } else if (errorCode === 409) {
      errorType = 'PACKING_LIST';
    } else if (errorCode === 412) {
      errorType = 'EMPTY_PDF_PAGE';
    } else if (errorCode === 413) {
      errorType = 'FILE_TOO_LARGE';
    } else if (errorCode === 415) {
      errorType = 'UNSUPPORTED_FILE';
    } else if (errorCode === 1001 || errorCode === 408 || (errorCode >= 500 && errorCode <= 599)) {
      errorType = 'SERVICE_UNAVAILABLE';
    }

    this.invoiceOcrErrorTitleKey = `COO.OCR.ERRORS.${errorType}.TITLE`;
    this.invoiceOcrErrorMessageKey = `COO.OCR.ERRORS.${errorType}.MESSAGE`;
    this.showInvoiceOcrErrorModal = true;
  }

  private getInvoiceOcrResponseCode(source: any): number | null {
    const rawCode = source?.code
      ?? source?.error?.code
      ?? source?.status
      ?? source?.error?.status
      ?? null;
    const parsedCode = Number(rawCode);
    return Number.isFinite(parsedCode) ? parsedCode : null;
  }

  private syncDialogState(
    dialogRef: ElementRef<HTMLDialogElement> | undefined,
    shouldOpen: boolean
  ) {
    const dialog = dialogRef?.nativeElement;
    if (!dialog) {
      return;
    }

    if (shouldOpen && !dialog.open) {
      dialog.showModal();
      return;
    }

    if (!shouldOpen && dialog.open) {
      dialog.close();
    }
  }

  private closeDialog(dialogRef: ElementRef<HTMLDialogElement> | undefined) {
    const dialog = dialogRef?.nativeElement;
    if (dialog?.open) {
      dialog.close();
    }
  }

  private clearInvoiceOcrTimers() {
    if (this.invoiceOcrUploadTimer) {
      window.clearInterval(this.invoiceOcrUploadTimer);
      this.invoiceOcrUploadTimer = undefined;
    }
    if (this.invoiceOcrScanTimer) {
      window.clearInterval(this.invoiceOcrScanTimer);
      this.invoiceOcrScanTimer = undefined;
    }
    if (this.invoiceOcrScanCompletionTimer) {
      window.clearTimeout(this.invoiceOcrScanCompletionTimer);
      this.invoiceOcrScanCompletionTimer = undefined;
    }
    if (this.invoiceOcrPollingTimer) {
      window.clearTimeout(this.invoiceOcrPollingTimer);
      this.invoiceOcrPollingTimer = undefined;
    }
  }
}
